/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@lib/api-client';
import { notifications } from '@mantine/notifications';

// I18n
import { useTranslation } from 'react-i18next';

// Types
import { IApiError, TParams } from '@global-types/index';
import { queryClient } from '@lib/react-query';
import useStore from '@store/index';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */

type TApiResponse = { id: string; active: boolean };

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const activateMeasurabilityProject = (data: {
	active: boolean;
	ids: string[];
}): Promise<TApiResponse> => {
	return apiClient.patch(
		`/measurabilities/activate`,
		data
	);
};

export const useActivateMeasurabilityProject = (
	page: number,
	size: number,
	queryParams: TParams,
	sortParams: string
) => {
	const { t } = useTranslation();
	const {
		setMeasurabilitiesSelectedRow,
		measurabilitiesSelectedRow,
	} = useStore();
	const {
		mutate,
		isPending: isLoading,
		isSuccess,
	} = useMutation({
		mutationFn: activateMeasurabilityProject,
		onSuccess: (response) => {
			if (response && Array.isArray(response)) {
				queryClient.invalidateQueries({
					queryKey: [
						'databases-measurabilities',
						{
							page: page,
							size: size,
							queryParams: queryParams,
							sortParams: sortParams,
						},
					],
				});
				setMeasurabilitiesSelectedRow({
					...measurabilitiesSelectedRow,
					active: !measurabilitiesSelectedRow.active,
				});
				notifications.show({
					title: 'Success',
					message: t(
						'notifications.statusProjectSuccessfullyUpdated'
					),
					color: 'success',
					autoClose: 1500,
				});
			}
		},
		onError: (error: IApiError) => {
			notifications.show({
				title: t('global.error'),
				message: t(`api.${error.type}`),
				color: 'error',
			});
		},
	});

	return { mutate, isLoading, isSuccess };
};
